import { useState } from 'react';
import MarkdownEditor from './MarkdownEditor';
import { useCurrentUser } from '../../context/CurrentUserContext';

const ALL_SECTIONS = [
  'Overview',
  'Severity Details',
  'Issues Details',
  'Fix Status',
  'Campaign Performance',
];

function CampaignForm({ campaign, onSave, onCancel, saving }) {
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.hubRole === 'admin';
  const isNew = !campaign.id;

  const activeSections = campaign.config?.sections?.map(s => s.name) ?? ALL_SECTIONS;

  const [form, setForm] = useState({
    campaignName:   campaign.campaignName   ?? '',
    campaignLabel:  campaign.campaignLabel  ?? '',
    startDate:      campaign.startDate      ?? '',
    endDate:        campaign.endDate        ?? '',
    leads:          campaign.leads          ?? '',
    description:    campaign.description    ?? '',
    confluenceUrl:  campaign.confluenceUrl  ?? '',
    confluenceName: campaign.confluenceName ?? '',
    slackUrl:       campaign.slackUrl       ?? '',
    slackName:      campaign.slackName      ?? '',
    confirmedBugsOverride: campaign.confirmedBugsOverride ?? '',
    sections:       activeSections,
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleSection = name =>
    set('sections', form.sections.includes(name)
      ? form.sections.filter(s => s !== name)
      : [...form.sections, name]);

  const handleSubmit = e => {
    e.preventDefault();
    const { sections, confirmedBugsOverride, ...rest } = form;
    const config = { sections: sections.map(name => ({ name, options: {} })) };
    const payload = {
      ...rest,
      startDate:      rest.startDate      || null,
      endDate:        rest.endDate        || null,
      leads:          rest.leads          || null,
      description:    rest.description    || null,
      confluenceUrl:  rest.confluenceUrl  || null,
      confluenceName: rest.confluenceName || null,
      slackUrl:       rest.slackUrl       || null,
      slackName:      rest.slackName      || null,
      config,
    };
    // Omit the key entirely for non-admins — the API treats its mere presence as an
    // attempt to set/clear the override and 403s write-role users for it.
    if (isAdmin) {
      payload.confirmedBugsOverride = confirmedBugsOverride === '' ? null : Number(confirmedBugsOverride);
    }
    onSave(payload);
  };

  return (
    <div className="cf-overlay">
      <div className="cf-modal">
        <div className="cf-header">
          <h2>{isNew ? 'New Campaign' : 'Edit Campaign'}</h2>
          <button className="cf-close" type="button" onClick={onCancel}>✕</button>
        </div>

        <form className="cf-form" onSubmit={handleSubmit}>
          <div className="cf-columns">

            {/* Left column — short fields */}
            <div className="cf-col">
              <label className="cf-label">Campaign Name <span className="cf-req">*</span></label>
              <input className="cf-input" required value={form.campaignName}
                onChange={e => set('campaignName', e.target.value)} />

              <label className="cf-label">Campaign Label <span className="cf-req">*</span></label>
              <input className="cf-input" required value={form.campaignLabel}
                onChange={e => set('campaignLabel', e.target.value)}
                placeholder="e.g. DisneyTestCampaign" />

              <div className="cf-row-2">
                <div>
                  <label className="cf-label">Start Date</label>
                  <input className="cf-input" type="date" value={form.startDate}
                    onChange={e => set('startDate', e.target.value)} />
                </div>
                <div>
                  <label className="cf-label">End Date</label>
                  <input className="cf-input" type="date" value={form.endDate}
                    onChange={e => set('endDate', e.target.value)} />
                </div>
              </div>

              <label className="cf-label">Leads</label>
              <input className="cf-input" value={form.leads}
                onChange={e => set('leads', e.target.value)}
                placeholder="Comma-separated names" />

              <label className="cf-label">Confluence URL</label>
              <input className="cf-input" type="url" value={form.confluenceUrl}
                onChange={e => set('confluenceUrl', e.target.value)}
                placeholder="https://confluence.example.com/…" />

              <label className="cf-label">Confluence Page Name</label>
              <input className="cf-input" value={form.confluenceName}
                onChange={e => set('confluenceName', e.target.value)}
                placeholder="e.g. Disney Campaign Overview" />

              <label className="cf-label">Slack URL</label>
              <input className="cf-input" type="url" value={form.slackUrl}
                onChange={e => set('slackUrl', e.target.value)}
                placeholder="https://slack.com/app_redirect?channel=…" />

              <label className="cf-label">Slack Channel Name</label>
              <input className="cf-input" value={form.slackName}
                onChange={e => set('slackName', e.target.value)}
                placeholder="#campaign-channel" />

              {isAdmin && (
                <>
                  <label className="cf-label">Confirmed Bugs Override</label>
                  <input className="cf-input cf-input-narrow" type="number" min="0" value={form.confirmedBugsOverride}
                    onChange={e => set('confirmedBugsOverride', e.target.value)}
                    placeholder="# Bugs" />
                </>
              )}

              <label className="cf-label">Visible Sections</label>
              <div className="cf-sections">
                {ALL_SECTIONS.map(name => (
                  <label key={name} className="cf-section-check">
                    <input type="checkbox" checked={form.sections.includes(name)}
                      onChange={() => toggleSection(name)} />
                    {name}
                  </label>
                ))}
              </div>
            </div>

            {/* Right column — description */}
            <div className="cf-col">
              <label className="cf-label">Description</label>
              <MarkdownEditor
                value={form.description}
                onChange={v => set('description', v)}
                rows={20}
              />
            </div>

          </div>

          <div className="cf-actions">
            <button type="button" className="cf-btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="cf-btn-save" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create Campaign' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CampaignForm;
